/**
 * Router für die Siqment SaaS-Anwendung (ES Module)
 */

export class Router {
    constructor() {
        this.routes = new Map();
        this.currentPage = null;
        this.container = null;
        this.beforeNavigateCallbacks = [];
        this.navigationRequestId = 0;
        this._boundHandleRoute = () => this.handleRoute();
        this._initialized = false;
        this.serviceContainer = null;
        this.pageOptionsResolver = null;
    }

    t(key, fallback, params = {}) {
        return (window.i18n && window.i18n.t)
            ? window.i18n.t(key, params)
            : fallback;
    }

    init(container, skipInitialRoute = false) {
        this.container = container;
        if (!this._initialized) {
            window.addEventListener('hashchange', this._boundHandleRoute);
            window.addEventListener('popstate', this._boundHandleRoute);
            this._initialized = true;
        }
        if (!skipInitialRoute) this.handleRoute();
    }

    start() {
        this.handleRoute();
    }

    register(path, pageDefinition) {
        this.routes.set(path, pageDefinition);
    }

    beforeNavigate(callback) {
        this.beforeNavigateCallbacks.push(callback);
    }

    setServiceContainer(serviceContainer) {
        this.serviceContainer = serviceContainer || null;
    }

    getServiceContainer() {
        return this.serviceContainer || null;
    }

    setPageOptionsResolver(resolver) {
        this.pageOptionsResolver = typeof resolver === 'function' ? resolver : null;
    }

    buildPageOptions(path, params = {}) {
        const resolvedOptions = this.pageOptionsResolver
            ? (this.pageOptionsResolver({ path, params }) || {})
            : {};

        const normalizedOptions = (resolvedOptions && typeof resolvedOptions === 'object')
            ? { ...resolvedOptions }
            : {};

        const dependencies = {
            ...(normalizedOptions.dependencies || {})
        };

        if (this.serviceContainer) {
            if (typeof this.serviceContainer.snapshot === 'function') {
                Object.assign(dependencies, this.serviceContainer.snapshot());
            }
            dependencies.appServices = this.serviceContainer;
        }

        normalizedOptions.dependencies = dependencies;
        return normalizedOptions;
    }

    async navigate(path, params = {}, updateHash = true) {
        for (const callback of this.beforeNavigateCallbacks) {
            const result = callback(path, params);
            if (result === false) {
                if (updateHash) {
                    const redirectPath = this.getRedirectPath(path);
                    if (redirectPath && redirectPath !== path) {
                        const url = this.buildUrl(redirectPath, params);
                        if (window.location.hash !== url) window.location.hash = url;
                    }
                }
                return;
            }
        }
        if (updateHash) {
            const url = this.buildUrl(path, params);
            if (window.location.hash !== url) {
                window.location.hash = url;
                return;
            }
        }
        const pageDefinition = this.routes.get(path);
        if (!pageDefinition) {
            console.warn(`⚠️ ${this.t('router.noRouteForPath', 'Keine Route gefunden für: {path}', { path })}`);
            const fallbackPath = this.getFallbackPath();
            if (path !== fallbackPath) void this.navigate(fallbackPath);
            return;
        }

        const requestId = ++this.navigationRequestId;

        if (this.currentPage && typeof this.currentPage.unmount === 'function') {
            try {
                const unmountResult = this.currentPage.unmount();
                if (unmountResult && typeof unmountResult.then === 'function') {
                    await unmountResult;
                }
            } catch (error) {
                console.error('❌ Fehler beim Unmount der aktuellen Seite:', error);
            }
        }
        this.currentPage = null;

        this.showRouteLoading();

        let PageClass;
        try {
            PageClass = await this.resolvePageClass(pageDefinition);
        } catch (error) {
            if (requestId !== this.navigationRequestId) return;
            console.error(`❌ Fehler beim Laden der Route "${path}":`, error);
            this.showRouteError(path);
            return;
        }

        if (requestId !== this.navigationRequestId) return;

        if (!this.isPageClass(PageClass)) {
            console.error(`❌ Ungültige Route-Definition für "${path}".`);
            this.showRouteError(path);
            return;
        }

        try {
            const pageOptions = this.buildPageOptions(path, params);
            this.currentPage = new PageClass(this, pageOptions);
            const mountResult = this.currentPage.mount(this.container, params);
            if (mountResult && typeof mountResult.then === 'function') {
                await mountResult;
            }

            if (requestId !== this.navigationRequestId) return;

            this.updateNavigation(path);
            if (typeof window.updateSidebarTranslations === 'function') {
                window.updateSidebarTranslations();
            }
        } catch (error) {
            if (requestId !== this.navigationRequestId) return;
            console.error(`❌ Fehler beim Mounten der Route "${path}":`, error);
            this.currentPage = null;
            this.showRouteError(path);
        }
    }

    getRedirectPath() { return 'login'; }
    getFallbackPath() { return 'login'; }

    handleRoute() {
        let hash = window.location.hash.slice(1);
        const [path, queryString] = hash.split('?');
        const params = this.parseQueryString(queryString);
        void this.navigate(path || 'data-sources', params, false);
    }

    updateNavigation(path) {
        document.querySelectorAll('.nav-link').forEach(link => {
            link.classList.remove('active');
            link.removeAttribute('aria-current');
        });
        const activeLink = document.querySelector(`[data-page="${path}"]`);
        if (activeLink) {
            activeLink.classList.add('active');
            activeLink.setAttribute('aria-current', 'page');
        }
    }

    parseQueryString(queryString) {
        if (!queryString) return {};
        const params = {};
        for (const [key, value] of new URLSearchParams(queryString)) params[key] = value;
        return params;
    }

    buildUrl(path, params = {}) {
        let url = `#${path}`;
        const queryParams = Object.entries(params)
            .filter(([_, v]) => v != null)
            .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
            .join('&');
        if (queryParams) url += `?${queryParams}`;
        return url;
    }

    getCurrentRoute() {
        const hash = window.location.hash.slice(1);
        return hash ? hash.split('?')[0] : 'data-sources';
    }

    isPageClass(candidate) {
        return typeof candidate === 'function' && typeof candidate.prototype?.mount === 'function';
    }

    async resolvePageClass(pageDefinition) {
        let definition = pageDefinition;

        if (definition && typeof definition === 'object' && typeof definition.load === 'function') {
            definition = definition.load;
        }

        if (this.isPageClass(definition)) {
            return definition;
        }

        if (typeof definition === 'function') {
            const loaded = await definition();
            return loaded?.default || loaded;
        }

        return definition?.default || definition;
    }

    showRouteLoading() {
        if (!this.container) return;
        this.container.innerHTML = `
            <div class="loading-state">
                <div class="modern-spinner"></div>
                <p>${this.t('router.loadingPage', 'Lade Seite...')}</p>
            </div>
        `;
    }

    showRouteError(path) {
        if (!this.container) return;
        this.container.innerHTML = `
            <div class="error-state">
                <h3>${this.t('router.errorTitle', 'Seite konnte nicht geladen werden')}</h3>
                <p>${this.t('router.errorRouteOpen', 'Die Route "{path}" konnte nicht geöffnet werden.', { path })}</p>
            </div>
        `;
    }

    isLoginRoute(route) { return route === 'login'; }
    isOnLoginPage() { return this.isLoginRoute(this.getCurrentRoute()); }
}

export const router = new Router();
